import React from 'react';

import { ArrowDownOutlined, ArrowUpOutlined, MinusOutlined } from '@ant-design/icons';
import { Card, Skeleton, Statistic, Tooltip } from 'antd';

export type DeltaTone = 'growth-good' | 'growth-neutral' | 'growth-bad';

type Props = {
  title: string;
  value: number | string;
  suffix?: string;
  precision?: number;
  delta?: number | null;
  deltaSuffix?: string;
  /** How to color delta: orders/suivi vs return-rate (where up is bad). */
  deltaTone?: DeltaTone;
  /** When false, the card keeps the same footer height but hides “Change”. */
  showDelta?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  /** Brand-hex accent used for the icon tint and the gradient highlight. */
  accent?: string;
};

const COLOR_NEUTRAL = '#8c8c8c';
const COLOR_UP_GOOD = '#52c41a';
const COLOR_DOWN_BAD = '#ff4d4f';
const COLOR_INFO = '#1677ff';

function deltaColor(tone: DeltaTone, up: boolean): string {
  if (tone === 'growth-good') return up ? COLOR_UP_GOOD : COLOR_DOWN_BAD;
  if (tone === 'growth-bad') return up ? COLOR_DOWN_BAD : COLOR_UP_GOOD;
  return COLOR_INFO;
}

function DeltaBadge({
  delta,
  suffix,
  tone,
}: {
  delta: number;
  suffix?: string;
  tone: DeltaTone;
}) {
  const flat = delta === 0;
  const up = delta > 0;
  const color = flat ? COLOR_NEUTRAL : deltaColor(tone, up);
  return (
    <span className="dashboard-kpi-card__delta" style={{ color }}>
      {flat && <MinusOutlined />}
      {!flat && up && <ArrowUpOutlined />}
      {!flat && !up && <ArrowDownOutlined />}{' '}
      {flat ? '0' : `${up ? '+' : ''}${delta}${suffix ?? '%'}`}
    </span>
  );
}

export const KpiCard = ({
  title,
  value,
  suffix,
  precision,
  delta,
  deltaSuffix,
  deltaTone = 'growth-neutral',
  showDelta = true,
  loading,
  icon,
  accent = COLOR_INFO,
}: Props) => (
  <Card
    bordered={false}
    className="dashboard-kpi-card"
    style={{
      // accent-tinted gradient; kept as inline style because the tint is
      // per-instance and not known at LESS compile time.
      background: `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, ${accent}14 100%)`,
    }}
  >
    {loading ? (
      <>
        <div className="dashboard-kpi-card__main">
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
        <div className="dashboard-kpi-card__delta-row" aria-hidden>
          <span className="dashboard-kpi-card__delta-spacer" />
        </div>
      </>
    ) : (
      <>
        <div className="dashboard-kpi-card__main">
          <Statistic
            title={
              <span className="dashboard-kpi-card__title">
                {icon ? (
                  <span className="dashboard-kpi-card__icon" style={{ color: accent }}>
                    {icon}
                  </span>
                ) : null}
                {title}
              </span>
            }
            value={value}
            suffix={suffix}
            precision={precision}
            valueStyle={{ fontSize: 28, fontWeight: 700 }}
          />
        </div>
        {/* Same footer slot on every card so heights align with the tallest */}
        <div className="dashboard-kpi-card__delta-row">
          {!showDelta || delta === null || delta === undefined ? (
            <span className="dashboard-kpi-card__delta-spacer" aria-hidden />
          ) : (
            <Tooltip title="Compared with the previous window of the same length">
              <span className="dashboard-kpi-card__delta-trigger">
                <span className="dashboard-kpi-card__delta-label">Change</span>
                <DeltaBadge delta={delta} suffix={deltaSuffix} tone={deltaTone} />
              </span>
            </Tooltip>
          )}
        </div>
      </>
    )}
  </Card>
);
